import React, { useState } from 'react';
import axios from 'axios';
import { FaPaperPlane, FaSpinner, FaCheckCircle, FaBug, FaLightbulb, FaEnvelope, FaHandshake } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || '';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    type: 'General',
    website: '' // Honeypot field - should remain empty
  });
  const [status, setStatus] = useState({ loading: false, success: false, error: '' });

  const contactTypes = [
    { value: 'General', label: 'General Inquiry', icon: <FaEnvelope /> },
    { value: 'Bug Report', label: 'Bug Report', icon: <FaBug /> },
    { value: 'Feature Request', label: 'Feature Request', icon: <FaLightbulb /> },
    { value: 'Partnership', label: 'Partnership', icon: <FaHandshake /> }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (status.error) {
      setStatus({ ...status, error: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: '' });

    try {
      const response = await axios.post(`${API_URL}/api/contact`, formData);
      if (response.data.success) {
        setStatus({ loading: false, success: true, error: '' });
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          type: 'General',
          website: ''
        });
        // Reset success message after 5 seconds
        setTimeout(() => setStatus({ loading: false, success: false, error: '' }), 5000);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to send message. Please try again.';
      setStatus({ loading: false, success: false, error: errorMessage });
    }
  };

  if (status.success) {
    return (
      <div className="text-center py-12">
        <FaCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
        <p className="text-gray-600">Thank you for reaching out. We'll get back to you soon.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Honeypot field - hidden from users, visible to bots */}
      <input
        type="text"
        name="website"
        value={formData.website}
        onChange={handleChange}
        style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      {/* Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">What can we help you with?</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {contactTypes.map((type) => (
            <button
              key={type.value}
              type="button"
              onClick={() => setFormData({ ...formData, type: type.value })}
              className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition ${
                formData.type === type.value
                  ? 'border-[#2186EB] bg-blue-50 text-[#2186EB]'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600'
              }`}
            >
              <span className="text-xl mb-1">{type.icon}</span>
              <span className="text-xs font-medium text-center">{type.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Name & Email Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            maxLength={100}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2186EB] focus:border-transparent transition"
            placeholder="Your name"
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            maxLength={100}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2186EB] focus:border-transparent transition"
            placeholder="your@email.com"
          />
        </div>
      </div>

      {/* Subject */}
      <div>
        <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
        <input
          type="text"
          id="subject"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          required
          maxLength={200}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2186EB] focus:border-transparent transition"
          placeholder="Brief description"
        />
      </div>

      {/* Message */}
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          maxLength={2000}
          rows={5}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2186EB] focus:border-transparent transition resize-none"
          placeholder="Tell us more..."
        />
        <p className="text-xs text-gray-400 mt-1">{formData.message.length}/2000 characters</p>
      </div>

      {/* Error Message */}
      {status.error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {status.error}
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={status.loading}
        className="w-full px-6 py-4 bg-[#FF7417] text-white rounded-lg font-semibold hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {status.loading ? (
          <>
            <FaSpinner className="animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <FaPaperPlane />
            Send Message
          </>
        )}
      </button>

      <p className="text-xs text-gray-500 text-center">
        We typically respond within 24-48 hours.
      </p>
    </form>
  );
};

export default ContactForm;
